import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getPost, formatPostDate, POSTS } from "@/content/blog";
import Seo, { SITE_URL, ORG_REF, BREADCRUMB_JSONLD } from "@/components/Seo";
import { aspectSize } from "@/lib/img";

// Prose may link to another page on the site with [anchor text](/path).
// Only site-relative paths are recognised, so body copy still cannot carry raw
// HTML or an off-site URL; anything else renders as the literal text it is.
const INLINE_LINK = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;
function Inline({ text }) {
  const out = [];
  let last = 0;
  for (const m of text.matchAll(INLINE_LINK)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <Link key={m.index} to={m[2]} className="text-foreground underline underline-offset-4 decoration-primary/50 hover:text-primary transition-colors">
        {m[1]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// "Keep reading" picks the next posts that share this post's tag, wrapping
// round the list, and tops up from the rest of the journal if the tag is thin.
// A fixed slice of POSTS would link every post to the same two articles and
// leave the rest reachable from /blog alone.
function relatedPosts(post) {
  const others = POSTS.filter((p) => p.slug !== post.slug);
  const at = POSTS.indexOf(post);
  const inOrder = [...POSTS.slice(at + 1), ...POSTS.slice(0, at)];
  const sameTag = inOrder.filter((p) => p.tag === post.tag);
  const picked = sameTag.slice(0, 2);
  for (const p of others) {
    if (picked.length >= 2) break;
    if (!picked.includes(p)) picked.push(p);
  }
  return picked;
}

function Block({ block }) {
  if (block.h2) return <h2 className="font-serif text-2xl md:text-3xl text-foreground mt-12 mb-4 tracking-tight">{block.h2}</h2>;
  if (block.p) return <p className="mb-5 leading-relaxed"><Inline text={block.p} /></p>;
  if (block.ul) return (
    <ul className="list-disc pl-6 space-y-2 mb-5">
      {block.ul.map((li, i) => <li key={i}><Inline text={li} /></li>)}
    </ul>
  );
  // Reference data belongs in a real <table>: answer engines and featured
  // snippets extract rows far more reliably than they do bulleted text.
  // `table` is { caption?, head: [...], rows: [[...], ...] }.
  if (block.table) {
    const { caption, head, rows } = block.table;
    return (
      <div className="overflow-x-auto mb-5">
        <table className="w-full text-left border-collapse">
          {caption ? <caption className="text-sm text-muted-foreground mb-2 text-left">{caption}</caption> : null}
          <thead>
            <tr className="border-b border-border">
              {head.map((h, i) => (
                <th key={i} scope="col" className="py-2 pr-4 font-medium text-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-border/50">
                {row.map((cell, j) => <td key={j} className="py-2 pr-4 align-top">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const more = relatedPosts(post);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover,
    datePublished: post.date,
    dateModified: post.updated || post.date,
    // Every post is written by the studio itself, so the author is the same
    // Organization node as the publisher, under its one canonical name. The
    // visible byline (post.author, "GetJewelDesigns Studio") is display copy and
    // must not leak in here as a second, differently named organisation.
    author: ORG_REF,
    publisher: {
      ...ORG_REF,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png` },
    },
    mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
  };

  // Posts that answer discrete questions can declare `faq: [{ q, a }]`.
  // FAQPage is what surfaces an answer in People Also Ask and in AI overviews,
  // so it is emitted only when the post genuinely poses and answers questions.
  const faqJsonLd = post.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map(({ q, a }) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      }
    : null;

  return (
    <article className="max-w-[800px] mx-auto px-4 md:px-8 py-12 md:py-20">
      {/* seoTitle/metaDescription are SEO-only overrides used where the editorial
          headline or standfirst would be truncated in a search result. The <h1>
          and standfirst below keep the full copy — see content/blog.js. */}
      <Seo
        title={post.seoTitle || post.title}
        description={post.metaDescription || post.excerpt}
        path={`/blog/${slug}`}
        image={post.cover}
        type="article"
        jsonLd={[
          articleJsonLd,
          BREADCRUMB_JSONLD([
            { name: "Journal", path: "/blog" },
            { name: post.title, path: `/blog/${slug}` },
          ]),
          ...(faqJsonLd ? [faqJsonLd] : []),
        ]}
      />
      <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-8 transition-colors">
        <ArrowLeft className="h-4 w-4" strokeWidth={1} /> All articles
      </Link>

      <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground mb-5">
        <span className="text-primary">{post.tag}</span>
        <span>·</span>
        <time dateTime={post.date}>{formatPostDate(post.date)}</time>
        {post.updated && (
          <>
            <span>·</span>
            <span>Updated <time dateTime={post.updated}>{formatPostDate(post.updated)}</time></span>
          </>
        )}
        <span>·</span>
        <span>{post.readingTime}</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl mb-6 leading-[1.1] tracking-tight">{post.title}</h1>
      <p className="text-muted-foreground text-lg leading-relaxed mb-10">{post.excerpt}</p>

      <div className="aspect-[16/9] overflow-hidden border hairline mb-12">
        <img src={post.cover} {...aspectSize("16/9", 1200)} alt={post.title} className="w-full h-full object-cover" />
      </div>

      <div className="text-muted-foreground">
        {post.body.map((block, i) => <Block key={i} block={block} />)}
      </div>

      <div className="mt-12 pt-8 border-t hairline text-xs uppercase tracking-widest text-muted-foreground">
        By {post.author}
      </div>

      {more.length > 0 && (
        <div className="mt-16">
          <h2 className="font-serif text-2xl mb-6">Keep reading</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {more.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group block">
                <div className="aspect-[16/10] overflow-hidden border hairline mb-4">
                  <img src={p.cover} {...aspectSize("16/10", 1200)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-80" />
                </div>
                {/* A link label, not a heading: these cards point at other posts,
                    so a question-shaped title here is not a section of this one. */}
                <p className="font-serif text-xl leading-tight group-hover:text-primary transition-colors duration-300">{p.title}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
